import {Button} from "primereact/button";
import {Message} from "primereact/message";
import {useTranslation} from "react-i18next";

interface GeneratedPasswordDisplayProps {
    /** Prefix for the element ids (e.g. "profileDialog" -> "profileDialogGeneratedHint" etc.). */
    idPrefix: string;
    password: string;
    copied: boolean;
    onCopy: () => void;
    hintKey: string;
    copyKey: string;
    copiedKey: string;
    /** Optional second way to unblock a caller that waits until the password is kept somewhere (#19351). */
    onNoted?: () => void;
    noted?: boolean;
    notedKey?: string;
}

/**
 * #19351: One-time display of a freshly generated password (used by {@link ProfileDialog}'s password
 * section and by `ResetPasswordPage`) — shows the value once with a "write it down now" hint and a
 * copy-to-clipboard button, since it can never be retrieved again afterwards.
 */
export default function GeneratedPasswordDisplay({
    idPrefix,
    password,
    copied,
    onCopy,
    hintKey,
    copyKey,
    copiedKey,
    onNoted,
    noted = false,
    notedKey,
}: GeneratedPasswordDisplayProps) {
    const {t} = useTranslation();
    return (
        <Message
            id={`${idPrefix}GeneratedHint`}
            severity="info"
            className="w-full"
            content={
                <div className="flex flex-col gap-2 p-2">
                    {/* <code> defaults to the browser's UA monospace font; align it with REVA's own font (#19351). */}
                    <code
                        id={`${idPrefix}GeneratedValue`}
                        className="font-bold text-base select-all [font-family:inherit]"
                    >
                        {password}
                    </code>
                    <div>{t(hintKey)}</div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            id={`${idPrefix}CopyBtn`}
                            type="button"
                            text
                            size="small"
                            icon="pi pi-copy"
                            label={t(copied ? copiedKey : copyKey)}
                            onClick={onCopy}
                        />
                        {onNoted && notedKey && (
                            <Button
                                id={`${idPrefix}NotedBtn`}
                                type="button"
                                text
                                size="small"
                                icon={noted ? "pi pi-check" : "pi pi-pencil"}
                                label={t(notedKey)}
                                disabled={noted}
                                onClick={onNoted}
                            />
                        )}
                    </div>
                </div>
            }
        />
    );
}
